<script>
  import { onMount } from "svelte";
  import * as d3 from "d3";

  import mainData from "../data/kpi_main_data.json";
  import detailsData from "../data/kpi_details_data.json";

  let isOpen = false;

  function openOverlay() {
    isOpen = true;
    document.body.style.overflow = "hidden";
  }

  function closeOverlay() {
    isOpen = false;
    document.body.style.overflow = "";
  }

  let mode = "total"; // total | year | month
  let selectedMonthIndex = 0;
  let selectedYearIndex = 0;
  
  $: selectedYear = years[selectedYearIndex];

  let currentData = {};
  let displayValues = {};

  $: yearPercent =
    years.length > 1
      ? (selectedYearIndex / (years.length - 1)) * 100
      : 0;

  $: monthPercent =
    months.length > 1
      ? (selectedMonthIndex / (months.length - 1)) * 100
      : 0;


  $: years = byYear
    ? byYear.map(d => d.year).sort((a, b) => a - b)
    : [];

  $: months = (mainData.by_month || [])
    .slice()
    .sort((a, b) => {
      if (a.year !== b.year) {
        return a.year - b.year;
      }
      return a.month - b.month;
    })
    .map(d => ({
      label: `${d.year}-${String(d.month).padStart(2, "0")}`,
      year: d.year,
      month: d.month
    }));

  // Year computation
  $: byYear = Object.values(
  (mainData.by_month || []).reduce((acc, d) => {
    if (!acc[d.year]) {
      acc[d.year] = {
        year: d.year,
        total_streams: 0,
        total_minutes: 0,
        total_songs: 0,
        total_artists: 0
      };
    }

    acc[d.year].total_streams += d.total_streams;
    acc[d.year].total_minutes += d.total_minutes;
    acc[d.year].total_songs += d.total_songs;
    acc[d.year].total_artists += d.total_artists;

    return acc;
  }, {})
);


  // Reactive Data Selection

  $: {
    if (mode === "total") {
      const main = (mainData.total || [])[0];
      const detail = (detailsData.total || [])[0];

      currentData = {
        ...(main || {}),
        
        top_artist: {
          name: detail.top_artist,
          streams: detail.art_str
        },

        top_song: {
          name: detail.top_song,
          artists: detail.top_song_art,
          streams: detail.top_song_str
        }
      };
    }

    if (mode === "year") {
      const main = (byYear || []).find(d => d.year === selectedYear);
      const artistDetail = (detailsData.by_year_artist || [])
        .find(d => d.year === selectedYear) || {};

      const songDetail = (detailsData.by_year_song || [])
        .find(d => d.year === selectedYear) || {};

      currentData = {
        ...(main || {}),
      
        top_artist: {
          name: artistDetail.top_artist,
          streams: artistDetail.str
        },

        top_song: {
          name: songDetail.song,
          artists: songDetail.top_artist,
          streams: songDetail.str
        }
      };
    }

    if (mode === "month") {
      const main = mainData.by_month[selectedMonthIndex] || {};
      const detail = detailsData.by_month[selectedMonthIndex];

      currentData = {
        ...main,

        top_artist: {
          name: detail.top_artist,
          streams: detail.art_str
        },

        top_song: {
          name: detail.top_song,
          artists: detail.top_song_art,
          streams: detail.top_song_str
        }
      };
    }

    animateValues();
  }

  // Count-up Animation

  function animateValues() {
    const duration = 600;

    const interpolators = {
      total_streams: d3.interpolateNumber(
        displayValues.total_streams || 0,
        currentData.total_streams || 0
      ),
      total_minutes: d3.interpolateNumber(
        displayValues.total_minutes || 0,
        currentData.total_minutes || 0
      ),
      total_songs: d3.interpolateNumber(
        displayValues.total_songs || 0,
        currentData.total_songs || 0
      ),
      total_artists: d3.interpolateNumber(
        displayValues.total_artists || 0,
        currentData.total_artists || 0
      )
    };

    d3.transition()
      .duration(duration)
      .tween("numbers", () => t => {
        displayValues = {
          total_streams: Math.floor(interpolators.total_streams(t)),
          total_minutes: Math.floor(interpolators.total_minutes(t)),
          total_songs: Math.floor(interpolators.total_songs(t)),
          total_artists: Math.floor(interpolators.total_artists(t))
        };
      });
  }



  const format = d3.format(",");

</script>

<div class="kpi-wrapper">
  <div class="kpi-controls">  
    <h1> Spotify Rewrapped:  2021 - 2025 </h1>
      <div class="top-buttons">
        <button class="desc" on:click={() => isOpen = true}>Description</button>
        <a href="https://younghoon-park.info/" target="_blank" class="btn">Back to Portfolio</a>
      </div>

      {#if isOpen}
        <div class="overlay" on:click={(e) => e.target === e.currentTarget && closeOverlay()}>
          <div class="overlay-content">
            <button class="close-btn" on:click={closeOverlay}>✕</button>

            <h2>Spotify Dashboard</h2>
            <p>
              This interactive dashboard, built with D3.js and Svelte, visualizes the 5-year streaming trends of my Spotify library.
              There are 4 visualizations.
            
            <br><br>
            <span class="stronger">1. KPI Dashboard</span>: Summarizes my streaming activity, showing total, yearly, and monthly counts 
            of stream minutes listened, unique tracks, and artists, along with my top song and top artist.<br>

            <span class="stronger">2. Artist Bubble Map</span>: Visualizes the geographic origin of my favorite artists with over 100 
            streams, and allows you to select a country to explore its top artists.<br>

            <span class="stronger">3. Language Graph</span>: A simple bar graph displaying the most streamed languages and highlighting
            patterns in my listening habits. Since English songs dominate my playlist, you can click "Hide English" to remove that bar.<br>

            <span class="stronger">4. Release Year Graph</span>: A simple bar graph showing how my streams are distributed across 
            different release years, revealing trends in my music consumption over time.<br><br>

            Hope you enjoy exploring this dashboard! :)<br>
            <span class="small">Younghoon Park, 19 March 2026</span>
            </p>

          </div>
        </div>
      {/if}

    <div class="slider-area">
      {#if mode === "year" && years.length}
        <div class="slider-container">
          <input
            type="range"
            min="0"
            max={years.length - 1}
            step="1"
            bind:value={selectedYearIndex}
            style="--fill:{yearPercent}%"
          />

          <div
            class="thumb-label"
            style="left: {yearPercent}%"
          >
            {selectedYear}
          </div>
        </div>
      {/if}

      {#if mode === "month" && months.length}
        <div class="slider-container">
          <input
            type="range"
            min="0"
            max={months.length - 1}
            step="1"
            bind:value={selectedMonthIndex}
            style="--fill:{monthPercent}%"
          />

          <div
            class="thumb-label"
            style="left: {monthPercent}%"
          >
            {months[selectedMonthIndex].label}
          </div>
        </div>
      {/if}
    </div>

    <div class="mode-toggle">
      <label>
        <input type="radio" bind:group={mode} value="total"/>
        <span>Total</span>
      </label>
      <label>
        <input type="radio" bind:group={mode} value="year"/>
        <span>Year</span>
      </label>
      <label>
        <input type="radio" bind:group={mode} value="month"/>
        <span>Month</span>
      </label>
    </div>
  </div>

  <div class="kpi-grid">
    <div class="card">
      <div class="value">{format(displayValues.total_streams)}</div>
      <div class="label">Streams</div>
    </div>

    <div class="card">
      <div class="value">{format(displayValues.total_minutes)}</div>
      <div class="label">Minutes Streamed</div>
    </div>

    <div class="card">
      <div class="value">{format(displayValues.total_songs)}</div>
      <div class="label">Different Tracks</div>
    </div>

    <div class="card">
      <div class="value">{format(displayValues.total_artists)}</div>
      <div class="label">Different Artists</div>
    </div>

    <div class="card">
      <div class="value">{currentData.top_artist && currentData.top_artist.name}</div>
      <div class="label">
        {format(currentData.top_artist && currentData.top_artist.streams || 0)} streams
      </div>
    </div>

    <div class="card">
      <div class="value">
        <div class="detail1">{currentData.top_song && currentData.top_song.name}</div>
        <div class="detail2">{currentData.top_song && currentData.top_song.artists}</div></div>
      <div class="label">
        {format(currentData.top_song && currentData.top_song.streams || 0)} streams
      </div>
    </div>
  </div>
</div>

<style>
  .kpi-wrapper {
    background: #202020;
    width: 100%;
    padding: 1.25rem;
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    box-shadow: 0 0 8px rgba(29, 185, 84, 0.4);
    gap: 1rem;
  }

  .kpi-controls {
    display: grid;
    grid-template-columns: auto auto 1fr auto auto;
    grid-template-areas: 
      "title buttons spacer slider toggle";
    align-items: center;
    gap: 1rem;
  }

  .kpi-controls::before {
    content: "";
    grid-area: spacer;
  }


  .top-buttons {
    margin-top: 6px;
    grid-area: buttons;
    display: flex;
    gap: 12px;
    
  }


  .slider-area {
    margin-top: 6px;
    grid-area: slider;
    display: flex;
    align-items: center;
    
    justify-self: end;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    /*align-items: center;*/
    gap: 1.25rem;
    align-items: stretch;
  }

  .desc {
    width: 5.5rem;
  }

  .btn {
    width: 6.5rem;
  }

  .desc, .btn {
    display: inline-block;
    padding: 0.35rem;
    background: #2A2A2A;
    border: none;
    
    border-radius: 14px;
    font-family: "Outfit", sans-serif;
    font-size: 0.75rem;
    text-align: center;
    text-decoration: none;
    color: #BBBBBB;
  }


  .desc:hover, .btn:hover {
    color: #FFFFFF;
    background-color: #303030;
    transition: all 0.4s ease;
  }

  h1 {
    grid-area: title;
    font-size: 1.75rem;
    font-family: "Outfit", sans-serif;
    color: #1DB954;
    font-weight: 900;
    margin-top: 0;
    margin-bottom: 0;
  }

  h2 {
    color: #1DB954;
    font-family: "Outfit", sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 0;
    /*white-space: nowrap;*/
  }

  p {
    color: #FFFFFF;
    font-family: "Outfit", sans-serif;
    font-size: 0.9rem;
    font-weight: 300;
    margin-top: 0;
  }

  p .stronger {
    font-weight: 600;
  }

  p .small {
    color: #BBBBBB;
    font-size: 0.7rem;
  }

  .overlay {
    position: fixed;     
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0.6);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
  }

  .hidden {
    display: none;
  }

  .overlay-content {
    background: #111;
    color: white;
    padding: 20px;
    border-radius: 1.25rem;
    width: 90%;
    max-width: 400px;
    position: relative;
  }

  .close-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    background: transparent;
    border: none;
    color: white;
    font-size: 20px;
    cursor: pointer;
  }

  .card {
    background: #101010;
    /*width: 10em;*/
    height: 5rem;
    padding: 1.25rem;

    display: flex;
    flex-direction: column;
    justify-content: center; 
    align-items: center;
    
    transition: background 0.2s ease, transform 0.2s ease;
    border: 1px solid transparent; /* important */
    border-radius: 12px;
  }

  .card:hover {
    background: #151515;
    border: 1px solid #1DB954;
    border-width: 1px;
    border-radius: 12px;
    box-shadow: 0 0 8px rgba(29, 185, 84, 0.4);

    transition: all 0.4s ease;
    /*transform: translateY(-2px);*/
  }

  .value {
    color: #1DB954;
    font-family: "Outfit", sans-serif;
    font-size: 1.4rem;
    font-weight: 600;
    text-align: center;
 }

  .label {
    color: #FFFFFF;
    font-family: "Outfit", sans-serif;
    font-size: 0.8rem;
    font-weight: 600;
    text-align: center;
  }

  .mode-toggle {
    grid-area: toggle;
    display: flex;
    gap: 0.5rem;
    margin-top: 1rem;
    
  }

  .mode-toggle span:hover {
    color: #FFFFFF;
    background-color: #303030;
    transition: all 0.4s ease;
  }

  .mode-toggle label {
    cursor: pointer;
  }

  .mode-toggle input {
    display: none;
  }

  .mode-toggle span {
    padding: 6px 12px;
    border-radius: 14px;
    font-family: "Outfit", sans-serif;
    font-size: 0.75rem;
    background: #2a2a2a;
    color: #bbb;
    transition: all 0.2s ease;
  }

  .mode-toggle input:checked + span {
    background: #1DB954;
    color: white;
  }

  .slider-container {
    position: relative;
    justify-self: end;
    width: 260px;
    margin: 4px 20px;
    animation: fadeIn 0.25s ease;
  }

  /* Track */
  .slider-container input[type="range"] {
    width: 100%;
    appearance: none;
    height: 4px;
    border-radius: 2px;
    background: linear-gradient(
      to right,
      #1DB954 0%,
      #1DB954 var(--fill),
      #444 var(--fill),
      #444 100%
    );
    outline: none;
  }

  /* Hide default thumb */
  .slider-container input[type="range"]::-webkit-slider-thumb {
    appearance: none;
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 50%;
    background: #202020;
    box-shadow: 0px 0px 2px 2px #1DB954;
    cursor: pointer;
  }

  .slider-container input[type="range"]::-moz-range-thumb {
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 50%;
    background: #202020;
    box-shadow: 0px 0px 2px 2px #1DB954;
    border: none;
    cursor: pointer;
  }

  /* Custom thumb label */
  .thumb-label {
    position: absolute;
    top: -1.5rem;
    transform: translateX(-50%);
    background: #2A2A2A;
    border: 1px solid #1DB954;
    color: #1DB954;
    font-family: "Outfit", sans-serif;
    font-size: 0.675rem;
    padding: 5px 10px;
    border-radius: 14px;
    pointer-events: none;
    transition: left 0.1s ease-out;
    
    box-shadow: 0 0 8px rgba(29, 185, 84, 0.4);
    white-space: nowrap;
  }

  .detail1 {
    font-size: 1.4rem;
  }

  .detail2 {
    font-size: 0.8rem;
    color: #CCCCCC;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    .kpi-controls {
      display: grid;
      grid-template-columns: 3fr 1fr;
      grid-template-areas:
        "title title"
        "slider toggle";
      gap: 0.75rem;
    }

    .kpi-controls h1 {
      grid-area: title;
    }

    .slider-area {
      grid-area: slider;
      justify-self: end;
    }

    .kpi-grid {
      grid-template-columns: repeat(3, 1fr);
      gap: 1.25rem;
      align-items: stretch;
    }

    .card {
      background: #101010;
      height: 4.5rem;
      padding: 1.25rem;

      display: flex;
      flex-direction: column;
      justify-content: center; 
      align-items: center;
    }

    .slider-container {
      width: 160px;
      margin: 4px 20px;
    }

    .mode-toggle {
      grid-area: toggle;
      justify-self: end;
    }
  }
  
  @media (max-width: 480px) {
    .kpi-wrapper {
      gap: 0rem;
    }

    .kpi-controls {
      grid-template-columns: 1fr 1fr;
      grid-template-areas:
        "title title"
        "buttons buttons"
        "slider toggle";
      gap: 0.5rem;
      column-gap: 0;
    }

    .kpi-controls h1 {
      grid-area: title;
      font-size: 1.25rem;
    }

    .top-buttons {
      grid-area: buttons;
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    }

    .slider-area {
      grid-area: slider;
      justify-self: start;
    }

    .kpi-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 0.75rem;
      align-items: stretch;
    }

    .btn {
      width: 5.5rem;
      font-size: 0.625rem;
      font-weight: 400;
    }

    .desc {
      width: 4.5rem;
      font-size: 0.625rem;
      font-weight: 400;
    }

    .card {
      background: #101010;
      height: 4rem;
      padding: 1rem;

      display: flex;
      flex-direction: column;
      justify-content: center; 
      align-items: center;
    }

    .slider-container {
      width: 100px;
      margin: 0px 20px;
    }

    .mode-toggle {
      margin-top: 6px;
      grid-area: toggle;
      display: flex;
      justify-content: flex-end;
      width: 100%;
    }

    .mode-toggle span {
      font-size: 0.625rem;
      font-weight: 400;
    }

    .value {
      font-size: 1.2rem;
    }

    .label {
      font-size: 0.75rem;
    }
    
    .detail1 {
      font-size: 0.9rem;
    }

    .detail2 {
      font-size: 0.625rem;
      color: #CCCCCC;
    }

    .thumb-label {
      top: -1.25rem;
      font-size: 0.6rem;
      padding: 4px 8px;
    }
  }
</style>